import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { RestaurantDemoState } from "./restaurant-sandbox";
import { saveRestaurantDemoState } from "./restaurant-sandbox";
import type { RestaurantDemoV5State } from "./restaurant-demo-v5-state";
import { saveRestaurantDemoV5State } from "./restaurant-demo-v5-state";
import { runRestaurantCustomerAgent } from "./restaurant-customer-agent-client";
import {
  appendRestaurantCustomerConversationMessage,
  canApplyRestaurantCustomerTurn,
  restaurantCustomerBusinessSnapshot,
  loadRestaurantCustomerConversation,
  requestRestaurantCustomerHandoff,
  saveRestaurantCustomerConversation,
  RESTAURANT_CUSTOMER_CONVERSATION_EVENT,
  type RestaurantCustomerConversationState,
} from "./restaurant-customer-conversation";

const QUICK_MESSAGES = Object.freeze([
  "¿Qué pizzas tienen disponibles?",
  "Quiero una Napolitana grande con extra muzzarella",
  "Quiero reservar una mesa mañana a las 21 para 4",
]);

function requestKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `customer-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function RestaurantCustomerWebchat({
  businessSlug,
  businessName,
  state,
  v5,
  onStateChange,
  focusComposer = false,
}: {
  readonly businessSlug: string;
  readonly businessName: string;
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly onStateChange: (state: RestaurantDemoState, v5: RestaurantDemoV5State) => void;
  readonly focusComposer?: boolean;
}) {
  const [conversation, setConversation] = useState<RestaurantCustomerConversationState>(() => loadRestaurantCustomerConversation(businessSlug, businessName));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runtimeLabel, setRuntimeLabel] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const submitLockRef = useRef(false);

  useEffect(() => {
    const reload = (event?: Event) => {
      const slug = event instanceof CustomEvent && typeof event.detail?.slug === "string" ? event.detail.slug : businessSlug;
      if (slug === businessSlug) setConversation(loadRestaurantCustomerConversation(businessSlug, businessName));
    };
    window.addEventListener(RESTAURANT_CUSTOMER_CONVERSATION_EVENT, reload);
    window.addEventListener("focus", reload);
    return () => {
      window.removeEventListener(RESTAURANT_CUSTOMER_CONVERSATION_EVENT, reload);
      window.removeEventListener("focus", reload);
    };
  }, [businessSlug, businessName]);

  useEffect(() => {
    const node = messagesRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [conversation.messages.length, busy]);

  useEffect(() => {
    if (focusComposer) window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [focusComposer]);

  const history = useMemo(() => conversation.messages
    .filter((message) => message.role !== "system")
    .slice(-16)
    .map((message) => ({ role: message.role === "customer" ? "customer" as const : "assistant" as const, text: message.text })), [conversation.messages]);

  async function sendText(rawText: string) {
    const text = rawText.trim();
    if (!text || busy || submitLockRef.current) return;
    submitLockRef.current = true;
    setError(null);
    setInput("");
    const turnRequestId = requestKey();
    const customerId = `customer-${turnRequestId}`;
    const afterCustomer = appendRestaurantCustomerConversationMessage({ slug: businessSlug, businessName, role: "customer", text, id: customerId });
    setConversation(afterCustomer);
    if (afterCustomer.routingMode === "human") {
      submitLockRef.current = false;
      window.setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }

    setBusy(true);
    try {
      // Compare persisted snapshots: hydrating an absent seed can generate fresh timestamps.
      const businessSnapshot = restaurantCustomerBusinessSnapshot(businessSlug);
      const result = await runRestaurantCustomerAgent({ requestId: turnRequestId, conversationId: afterCustomer.conversationId, workingState: afterCustomer.workingState, message: text, history, state, v5 });
      const current = loadRestaurantCustomerConversation(businessSlug, businessName);
      if (!canApplyRestaurantCustomerTurn(afterCustomer, current) || restaurantCustomerBusinessSnapshot(businessSlug) !== businessSnapshot) {
        setConversation(current);
        setRuntimeLabel("La conversación cambió mientras respondía el asistente.");
        return;
      }
      saveRestaurantCustomerConversation(businessSlug, { ...current, workingState: result.workingState ?? current.workingState ?? null });
      saveRestaurantDemoState(businessSlug, result.state);
      saveRestaurantDemoV5State(businessSlug, result.v5);
      onStateChange(result.state, result.v5);
      setRuntimeLabel(`${result.runtime.mode === "real" ? "IA real" : "Fallback seguro"}${result.runtime.model ? ` · ${result.runtime.model}` : ""}`);
      if (result.handoffRequested) {
        const next = requestRestaurantCustomerHandoff({ slug: businessSlug, businessName, reason: result.response, bridgeId: `handoff-${turnRequestId}` });
        setConversation(next);
      } else {
        const next = appendRestaurantCustomerConversationMessage({ slug: businessSlug, businessName, role: "assistant", text: result.response, id: `assistant-${turnRequestId}` });
        setConversation(next);
      }
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "No pudimos contactar al asistente.";
      setError(message);
      setRuntimeLabel("Asistente no disponible");
    } finally {
      setBusy(false);
      submitLockRef.current = false;
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void sendText(input);
  }

  function handoff() {
    const next = requestRestaurantCustomerHandoff({ slug: businessSlug, businessName, reason: "El cliente pidió hablar con una persona." });
    setConversation(next);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  return <section className="rcx-webchat" aria-label={`Asistente de ${businessName}`} aria-busy={busy}>
    <header className="rcx-webchat-header">
      <div className="rcx-chat-avatar" aria-hidden="true">AI</div>
      <div><strong>Asistente de {businessName}</strong><span>{conversation.routingMode === "human" ? "Atención humana activa" : busy ? "Escribiendo…" : "En línea"}</span></div>
      <span className={`rcx-routing-badge ${conversation.routingMode}`}>{conversation.routingMode === "human" ? "Humano" : "IA"}</span>
    </header>

    <div className="rcx-chat-messages" ref={messagesRef} aria-live="polite" aria-relevant="additions text">
      {conversation.messages.map((message) => <div key={message.id} className={`rcx-chat-message ${message.role}`}><span>{message.text}</span><small style={{ color: "#5f584f" }}>{message.role === "operator" ? "Equipo" : message.role === "system" ? "Sistema" : message.role === "customer" ? "Vos" : "Asistente"}</small></div>)}
      {busy ? <div className="rcx-chat-message assistant typing"><span>•••</span><small style={{ color: "#5f584f" }}>Asistente</small></div> : null}
    </div>

    {conversation.routingMode === "ai" && conversation.messages.length <= 2 ? <div className="rcx-quick-replies">{QUICK_MESSAGES.map((message) => <button type="button" key={message} onClick={() => void sendText(message)}>{message}</button>)}</div> : null}
    {error ? <div className="rcx-error" role="alert">{error}</div> : null}
    {runtimeLabel ? <div className="rcx-chat-runtime" style={{ color: "#5f584f" }}>{runtimeLabel}</div> : null}

    {conversation.routingMode === "human" ? <div className="rcx-human-wait"><strong>El asistente está pausado.</strong><span>Podés seguir escribiendo; una persona del equipo ve esta conversación desde Administración.</span></div> : null}

    <form className="rcx-chat-composer" onSubmit={submit}>
      <label className="rcx-sr-only" htmlFor={`restaurant-customer-message-${businessSlug}`}>Mensaje</label>
      <input ref={inputRef} id={`restaurant-customer-message-${businessSlug}`} value={input} onChange={(event) => setInput(event.target.value)} placeholder={conversation.routingMode === "human" ? "Escribile al equipo…" : "Preguntá por la carta, pedidos o reservas…"} disabled={busy} autoComplete="off" />
      <button className="rcx-primary" type="submit" disabled={busy || !input.trim()}>{busy ? "…" : "Enviar"}</button>
    </form>
    {conversation.routingMode === "ai" ? <button className="rcx-human-link" type="button" onClick={handoff}>Prefiero hablar con una persona</button> : null}
  </section>;
}
